import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { addCurrentThemeClassToEle } from "@/utils";
import { useMediaQuery } from "@/pages/notification/useMediaQuery";
import { getDashboardDetailsApiCall } from "@/services/api";
import { setDashboardDetails } from "@/store/slices/dashboard/dashboardDetailsSlice";
import {
  handleUpdateUserPurchaseContentApiCall,
  handleUpdateWelcomeSeriesContentApiCall,
} from "@/utils/apiUtils";
import VeryTopNavbar from "@/components/common/veryTopNavbar";
import FloatingSupportButtons from "@/components/common/floatingSupportButtons";
import { ArrowLeft, ChevronLeft, ExternalLink } from "lucide-react";
import "./style.css";
import { useSafeAreaInsets } from "@/hooks/useSafeAreaInsets";

export default function ResourceRoute() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const currentTheme = useSelector((state: RootState) => state.currentThemeReducer.currentTheme);
  const dashboardDetails = useSelector((state: RootState) => state.dashboardDetailsReducer.dashboardDetails);
  const { topSafeComponent, bottomSafeComponent } = useSafeAreaInsets();
  const studyResources = dashboardDetails?.studyResources ?? [];

  const resource = useMemo(() => {
    if (!id) return undefined;
    return studyResources.find((r: any) => r._id === id);
  }, [id, studyResources]);

  useEffect(() => {
    const hasResources = Array.isArray(dashboardDetails?.studyResources);
    if (hasResources && dashboardDetails.studyResources.length > 0) return;

    const fetchDashboard = async () => {
      const payload = { purchaseId: null };
      const ok1 = await handleUpdateUserPurchaseContentApiCall(payload);
      const ok2 = await handleUpdateWelcomeSeriesContentApiCall(payload);
      if (!ok1 || !ok2) return;

      const res = await getDashboardDetailsApiCall();
      if (res?.status && res?.data) {
        dispatch(setDashboardDetails(res.data));
      }
    };

    fetchDashboard();
  }, [dispatch, dashboardDetails]);

  const links = Array.isArray(resource?.notesAndLinks) ? resource?.notesAndLinks : [];

  const onBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  return (
    <div className={`resourcePage ${addCurrentThemeClassToEle(currentTheme, "resourcePage")}`}>
      {/* <VeryTopNavbar showRhs={true} /> */}
      {topSafeComponent}
      <header className={`resourceHeaderStrip mt-[45px] min-[1280px]:mt-[0px] ${addCurrentThemeClassToEle(currentTheme, 'miniNav')}`}>
        <div className="resourceColumn">
          <div className="resourceTopRow">
            <button type="button" onClick={onBack} aria-label="Back" className="resourceHeaderBackBtn ms-[-12px]">
              <ArrowLeft
                className="resourceHeaderBackBtnIcon resourceHeaderBackBtnIcon--desktop"
                size={20}
                strokeWidth={2}
                aria-hidden
              />
              <ChevronLeft
                className="resourceHeaderBackBtnIcon resourceHeaderBackBtnIcon--mobile"
                size={20}
                strokeWidth={2}
                aria-hidden
              />
            </button>
            <p className="resourceTopTitle">Study Resources</p>
          </div>
        </div>
      </header>

      <div className="resourceMain">
        <div className="resourceColumn resourceContent">
          <header className="resourceHeader">
            <h1 className="resourceTitle">{resource?.title ?? "Resource"}</h1>
            <p className="resourceSubtitle">
              {links.length} {links.length === 1 ? "study material" : "study materials"} available
            </p>
          </header>

          <div className={isDesktop ? "resourceGrid" : "resourceList"} role="list">
            {links.map((nl: any) => (
              <button
                key={nl._id ?? nl.link}
                type="button"
                className="resourceItem"
                role="listitem"
                onClick={() => nl.link && window.open(nl.link, "_blank", "noopener,noreferrer")}
                disabled={!nl.link}
              >
                <span className="resourceItemTitle">{nl.noteName}</span>
                <span className="resourceItemIconWrap" aria-hidden>
                  <ExternalLink className="resourceItemIconSvg" size={16} strokeWidth={2} />
                </span>
              </button>
            ))}
          </div>
          {bottomSafeComponent}
        </div>
      </div>

      <FloatingSupportButtons />
    </div>
  );
}

